import React, { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import type { MachineNameAndSourceId } from "@common/types/server-list-types";
import { DashboardEditWidgetDrawerDropdown } from "@src/components/atoms/dashboard-edit-widget-drawer-dropdown/dashboard-edit-widget-drawer-dropdown";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { ScaleInput } from "@src/components/atoms/scale-input/scale-input";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import {
  ColorSwatchPicker,
  pickPaletteHex,
  TREND_LINE_PALETTE,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { MULTI_LINE_DEFAULT_COLORS } from "@src/components/organisms/widgets/multi-line/build-multi-line-option";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import Spinner from "@src/components/atoms/spinner/spinner";
import { DatePicker } from "@src/components/atoms/date-picker/date-picker";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import closeIcon from "@src/assets/svg/close-icon.svg";
import {
  DEFAULT_POLLING_INTERVAL,
  EVENT_TYPE_OPTIONS,
  LINE_CHART_DURATIONS_MS,
  POLLING_INTERVALS,
  durationToIso,
  isoToDurationMs,
  buildPollingOptions,
  findPollingOption,
} from "@src/components/organisms/widgets/constants";
import {
  buildShotDataLineChartQuery,
  getWidgetWindow,
} from "@src/components/organisms/widgets/widget-query";
import { getEventsForSourceUseCase } from "@src/cloud-connect/use-cases/get-events-for-source";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";
import {
  JsonFieldTree as JsonFieldTreeData,
  buildJsonFieldTree,
} from "@src/components/organisms/widgets/shot-data-line-chart/build-json-field-tree";
import { JsonFieldTree } from "@src/components/organisms/widgets/shot-data-line-chart/json-field-tree";
import {
  EncodingTransform,
  FieldType,
  GenericWidgetSemantics,
  QueryWindow,
  RoleBinding,
  Widget,
  WidgetSemantics,
} from "@src/types/dashboards";
import {
  selectUnitsById,
  selectUnitsStatus,
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitOptionLabelOf } from "@src/cloud-connect/units/utils/unit-option-label-of";
import type { UnitDefinition } from "@src/types/units";
import {
  scaleForField,
  scaleTransformsKey,
} from "@src/components/organisms/widgets/utils/value-scaling";
import { parseUserNumber } from "@src/components/organisms/widgets/utils/parse-user-number";
import { axisBoundsInputError } from "@src/components/organisms/widgets/axis-bounds";
import { buildDurationOptions } from "@src/components/organisms/widgets/utils/build-duration-options";
import { findDurationOption } from "@src/components/organisms/widgets/utils/find-duration-options";
import { useSaveWidget } from "../hooks/use-save-widget";
import styles from "./shot-data-line-chart-editor.module.scss";

/** Series cap, mirroring the template's `max_roles`. */
export const MAX_SHOT_DATA_SERIES = 5;

const NS = "modules.shotDataLineChart";

export interface ShotDataSeries {
  /** JSON Pointer into one event document — saved as the binding address. */
  address: string;
  /** Resolved series name: the custom label, else the JSON key. */
  label: string;
  /** Line colour. Always set for a line the user picked or ticked; null only
   *  for a line saved before colours existed (the chart then falls back to the
   *  palette by series position). */
  color: string | null;
  /** Multiplier applied to this series' numeric values. Omitted when 1
   *  (the default) so a widget the user never scaled keeps the same
   *  saved shape as before this feature. */
  scale?: number;
}

/** What decides the chart's x-axis window. */
export type ShotDataRangeMode = "duration" | "timeframe" | "events";

interface ShotDataRange {
  mode: ShotDataRangeMode;
  durationMs: number | null;
  from: string | null;
  to: string | null;
  /** Kept as typed: a blank or half-written count has to be reportable. */
  eventCount: string;
}

/** The saved range, else the first duration (same default as the other line
 *  charts). A saved count or timeframe is what reopens the matching mode. */
const initialRange = (widget: Widget): ShotDataRange => {
  const win = getWidgetWindow(widget.query);
  return {
    mode:
      win?.size != null
        ? "events"
        : win?.from || win?.to
        ? "timeframe"
        : "duration",
    // Always set, even while another mode is active: switching to duration
    // then opens on a usable value instead of an empty dropdown.
    durationMs: isoToDurationMs(win?.duration) ?? LINE_CHART_DURATIONS_MS[0].ms,
    from: win?.from ?? null,
    to: win?.to ?? null,
    eventCount: win?.size != null ? String(win.size) : "",
  };
};

/** Only the keys the chosen mode owns are written: the range control is one
 *  mode at a time, so a switch must not leave the previous mode's keys behind. */
const toWindow = (range: ShotDataRange): QueryWindow => {
  if (range.mode === "events") {
    return { size: Number(range.eventCount) };
  }
  if (range.mode === "timeframe") {
    return {
      from: range.from ?? undefined,
      to: range.to ?? undefined,
    };
  }
  return {
    duration:
      range.durationMs != null ? durationToIso(range.durationMs) : undefined,
  };
};

interface ShotDataLineChartEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

export const ShotDataLineChartEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: ShotDataLineChartEditorProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();
  const units = useSelector(selectUnitsById);
  const unitsStatus = useSelector(selectUnitsStatus);
  const isLoadingUnits = unitsStatus === "idle" || unitsStatus === "loading";

  const [title, setTitle] = useState(() => widget.title ?? "");
  // Events widgets address the machine directly (`query.source`) and hold no
  // node binding, so the seed hands the id forward and an effect below swaps
  // in the human name once the sources list lands.
  const [machine, setMachine] = useState<MachineNameAndSourceId | null>(() => {
    const id = widget.query?.source ?? null;
    return id ? { name: id, source_id: id } : null;
  });
  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  // The form stays hidden until the machines and units land: the machine seed
  // carries the raw source id, and showing it would flash the id before the
  // sources list swaps in the real name.
  const [isInitializing, setIsInitializing] = useState(true);
  // A fresh widget starts with none: the event type is never pre-picked, the
  // user chooses it.
  const [eventType, setEventType] = useState<string | null>(
    () => widget.query?.event_type ?? null,
  );
  const [unit, setUnit] = useState<UnitDefinition | null>(() =>
    isPendingWidget
      ? null
      : getUnitById(units, widget.presentation?.unit ?? null),
  );
  // Manual Y range: off = automatic axis (the default). A saved pair means the
  // user had it on.
  const [manualYRange, setManualYRange] = useState(() => {
    const s = widget.semantics as Partial<GenericWidgetSemantics>;
    return s.min_value != null && s.max_value != null;
  });
  const [min, setMin] = useState(() => {
    const s = widget.semantics as Partial<GenericWidgetSemantics>;
    return s.min_value != null ? String(s.min_value) : "";
  });
  const [max, setMax] = useState(() => {
    const s = widget.semantics as Partial<GenericWidgetSemantics>;
    return s.max_value != null ? String(s.max_value) : "";
  });

  // Saved series, captured once. Addresses come from the bindings, names from
  // the matching `value_N` roles — a widget saved before this editor existed
  // still reopens with its series selected.
  const [checkedIds, setCheckedIds] = useState<string[]>(() =>
    // Deduped and capped at seed time: a hand-edited widget must not start
    // with repeated pointers (duplicate React keys) or more series than the
    // payload is allowed to carry (which no later action could reduce).
    Array.from(
      new Set(
        (widget.query?.bindings ?? [])
          .map((binding) => binding.address)
          .filter(Boolean),
      ),
    ).slice(0, MAX_SHOT_DATA_SERIES),
  );
  const [labels, setLabels] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    const roles = widget.encoding?.roles ?? {};
    (widget.query?.bindings ?? []).forEach((binding, index) => {
      const role = roles[`value_${index}`];
      if (binding.address && role && !Array.isArray(role) && role.label) {
        out[binding.address] = role.label;
      }
    });
    return out;
  });
  // Line colours, keyed by pointer. Every line has one: the saved colour, else
  // a random palette colour assigned here and saved with the widget, so what
  // the editor shows is what the chart draws and reopening changes nothing.
  const [colors, setColors] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    const roles = widget.encoding?.roles ?? {};
    (widget.query?.bindings ?? []).forEach((binding, index) => {
      if (!binding.address) return;
      const role = roles[`value_${index}`];
      const saved = !Array.isArray(role) && role?.color ? role.color : null;
      // A saved colour wins; otherwise keep the colour the chart already draws
      // (its positional fallback) so opening and saving an older widget does
      // not silently recolour it. Random colours come from ticking a new field.
      out[binding.address] =
        saved ?? MULTI_LINE_DEFAULT_COLORS[index] ?? pickPaletteHex();
    });
    return out;
  });
  // Per-series scale inputs, keyed by JSON pointer. Text so a German comma
  // decimal types naturally; parsed into the saved `encoding.transforms` at
  // save time. Seeded from the saved transform for each binding slot.
  const [scales, setScales] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    (widget.query?.bindings ?? []).forEach((binding, index) => {
      if (!binding.address) return;
      const value = scaleForField(
        `value_${index}`,
        widget.encoding?.transforms,
      );
      if (value !== 1) out[binding.address] = String(value);
    });
    return out;
  });
  const [tree, setTree] = useState<JsonFieldTreeData | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "empty">(
    "idle",
  );
  const [range, setRange] = useState<ShotDataRange>(() => initialRange(widget));
  // Polling cadence for the shot-data fetch. Restored from an existing
  // widget's `query.sampling.polling_interval` (if it's a known preset), and
  // defaulted to `DEFAULT_POLLING_INTERVAL` for fresh widgets — this gives a
  // saved widget the right cadence on re-open, AND keeps the dropdown in a
  // sensible state on first open. X-clear sets the state to `null` so the
  // save path can default at write time, mirroring trend-line.
  const [pollingInterval, setPollingInterval] = useState<string | null>(() => {
    const saved = widget?.query?.sampling?.polling_interval;
    if (saved == null) return DEFAULT_POLLING_INTERVAL;
    return POLLING_INTERVALS.includes(
      saved as (typeof POLLING_INTERVALS)[number],
    )
      ? (saved as string)
      : DEFAULT_POLLING_INTERVAL;
  });

  // Load machines once.
  useEffect(() => {
    setIsLoadingMachines(true);
    getAllSourcesListUseCase().then((result) => {
      if (result.success && result.data) {
        setMachineOptions(
          Object.values(result.data).map((s) => ({
            name: s.name,
            source_id: s.source_id,
          })),
        );
      } else {
        setMachineOptions([]);
        console.error("Failed to load machines:", result.error);
      }
      setIsLoadingMachines(false);
    });
  }, []);

  // The sources list loads asynchronously, so the seed hands the machine id
  // forward and this effect swaps in the real name once the options arrive.
  useEffect(() => {
    if (machineOptions.length === 0 || !machine) return;
    const match = machineOptions.find((m) => m.source_id === machine.source_id);
    if (!match) return;
    setMachine((prev) => {
      if (!prev) return prev;
      // If the user is currently looking at a DIFFERENT machine, do NOT
      // overwrite — the prev.source_id test stops that race.
      if (prev.source_id !== match.source_id) return prev;
      if (prev.name === match.name) return prev;
      return { name: match.name, source_id: match.source_id };
    });
  }, [machineOptions, machine]);

  // Units come from the global slice (loaded at dashboards-load). Lazy
  // fallback: if the slice was never attempted (drawer opened before the
  // dashboards load), fetch once so the dropdown isn't empty.
  const unitsAttempted = useRef(false);
  useEffect(() => {
    if (unitsStatus !== "idle" || unitsAttempted.current) return;
    unitsAttempted.current = true;
    dispatch(unitsRequested());
    getUnitsUseCase().then((result) => {
      if (result.success && result.data) {
        dispatch(unitsLoaded(result.data));
      } else {
        dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
      }
    });
  }, [unitsStatus, dispatch]);

  // Restore the saved unit once the catalog is available (the global load may
  // still be in flight when the editor opens).
  useEffect(() => {
    if (isPendingWidget) return;
    const id = widget.presentation?.unit;
    if (id == null) return;
    const match = getUnitById(units, id);
    if (match) setUnit(match);
  }, [units, widget, isPendingWidget]);

  // Reveal the form once the machines list and the unit catalog are done.
  useEffect(() => {
    if (!isInitializing) return;
    if (!isLoadingMachines && !isLoadingUnits) {
      setIsInitializing(false);
    }
  }, [isLoadingMachines, unitsStatus, isInitializing]);

  const sourceId = machine?.source_id ?? null;

  useEffect(() => {
    if (!sourceId || !eventType) {
      setTree(null);
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    getEventsForSourceUseCase({ source: sourceId, eventType, size: 1 }).then(
      (result) => {
        if (cancelled) return;
        const events: unknown[] = Array.isArray(result.data) ? result.data : [];
        // The tree describes the NEWEST event: that is what the user picks
        // fields from, and the renderer then reads each pointer from every
        // event in the window.
        const newestEvent = events[events.length - 1];
        if (!result.success || newestEvent == null) {
          if (!result.success) {
            console.error("Failed to load event fields:", result.error);
          }
          setTree(null);
          setStatus("empty");
          return;
        }
        setTree(buildJsonFieldTree(newestEvent));
        setStatus("ready");
      },
    );
    return () => {
      cancelled = true;
    };
  }, [sourceId, eventType]);

  // Clear the picks when the user changes the machine OR the event type:
  // both decide which fields exist, so pointers picked under one reading
  // mean nothing under another. A widget switch remounts this editor (keyed
  // by widget id), so seeding stays a mount-time concern.
  const clearPicks = (): void => {
    setCheckedIds([]);
    setLabels({});
    setColors({});
    setScales({});
  };
  const handleMachineChange = (next: MachineNameAndSourceId | null): void => {
    if (next?.source_id === machine?.source_id) return;
    setMachine(next);
    // Kept from the drawer: the event type never survives a machine change.
    setEventType(null);
    clearPicks();
  };
  const handleEventTypeChange = (next: string | null): void => {
    if (next === eventType) return;
    setEventType(next);
    clearPicks();
  };

  // The unit picker accepts either the unit entry key OR the unit_id itself,
  // so a pick resolves against the slice whichever shape the option carries.
  const handleUnitSelection = (selected: MachineNameAndSourceId | null) => {
    setUnit(
      selected
        ? units[selected.source_id] ??
            Object.values(units).find(
              (candidate) => candidate.unit_id === selected.source_id,
            ) ??
            null
        : null,
    );
  };

  // The selection the current payload can actually show, IN THE ORDER THE USER
  // ADDED IT (`checkedIds` is append-only, so a field ticked later gets the
  // last row). An id the newest event does not carry gets no row in the tree,
  // but stays selected and is still saved, so a transient payload cannot drop
  // a series.
  const visibleCheckedIds = useMemo(
    () => checkedIds.filter((id) => Boolean(tree?.items[id])),
    [checkedIds, tree],
  );

  const resolvedLabel = (id: string): string =>
    labels[id] ?? tree?.items[id]?.label ?? id.slice(id.lastIndexOf("/") + 1);

  const buildSeries = (): ShotDataSeries[] => {
    // Visible series first, then the orphans (saved pointers the newest event
    // does not carry, which stay part of the payload).
    const ordered = [
      ...visibleCheckedIds,
      ...checkedIds.filter((id) => !tree?.items[id]),
    ];
    return ordered.map((id) => {
      const scale = parseUserNumber(scales[id] ?? "1") ?? 1;
      return {
        address: id,
        label: resolvedLabel(id),
        color: colors[id] ?? null,
        // Keep the saved shape lean: a default factor writes no `scale` key.
        ...(scale === 1 ? {} : { scale }),
      };
    });
  };

  // Read once per render: the counter, the name list and the save path all
  // describe the same series.
  const series = buildSeries();

  const validate = (): string | null => {
    if (title.trim() === "") return i18n(`${NS}.titleRequired`);
    if (!machine) return i18n(`${NS}.machineRequired`);
    if (!eventType) return i18n(`${NS}.eventTypeRequired`);
    if (series.length === 0) return i18n(`${NS}.fieldRequired`);
    // Unreachable while `handleCheckedIdsChange` clamps, kept as the second
    // line of defence for the payload that claims `max_roles: 5`.
    if (series.length > MAX_SHOT_DATA_SERIES)
      return i18n(`${NS}.tooManyFields`);
    // The window is what the chart reads, so a half-set one must not be
    // saved (same contract as the other line charts).
    if (range.mode === "timeframe") {
      if (!range.from || !range.to) return i18n(`${NS}.timeframeRequired`);
    } else if (range.mode === "events") {
      // A blank count is not a special case: `Number("")` is 0, which the
      // `count <= 0` clause rejects along with anything non-integer.
      const count = Number(range.eventCount);
      if (!Number.isInteger(count) || count <= 0) {
        return i18n(`${NS}.eventCountRequired`);
      }
    } else if (range.durationMs == null) {
      return i18n(`${NS}.durationRequired`);
    }
    const seen = new Set<string>();
    for (const item of series) {
      const name = item.label.trim();
      if (name === "") return i18n(`${NS}.labelRequired`);
      if (seen.has(name)) return i18n(`${NS}.labelDuplicate`);
      seen.add(name);
    }
    // Scale inputs must be blank (default 1) or a real number.
    for (const id of checkedIds) {
      const raw = scales[id] ?? "1";
      if (raw.trim() !== "" && parseUserNumber(raw) === null) {
        return i18n(`${NS}.scaleInvalid`);
      }
    }
    if (manualYRange) {
      const boundsError = axisBoundsInputError(min, max);
      if (boundsError === "required") return i18n(`${NS}.yAxisBoundsRequired`);
      if (boundsError === "invalid") return i18n(`${NS}.yAxisRangeInvalid`);
    }
    return null;
  };

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    // The thresholds, the target and the aggregated-bounds toggles have no
    // control here: they round-trip exactly as the BE returned them, with the
    // same normalisation the drawer applied when it prefilled and wrote them
    // back (a non-numeric saved value is dropped, not passed through).
    const saved = widget.semantics as Partial<GenericWidgetSemantics> & {
      target_manual?: number | null;
      thresholds?: {
        warning?: number | null;
        critical?: number | null;
      } | null;
    };
    const warning =
      saved.thresholds?.warning != null
        ? parseUserNumber(String(saved.thresholds.warning))
        : null;
    const critical =
      saved.thresholds?.critical != null
        ? parseUserNumber(String(saved.thresholds.critical))
        : null;
    const thresholds = {
      ...(warning != null ? { warning } : {}),
      ...(critical != null ? { critical } : {}),
    };
    // `state_map` is only meaningful for state-indicator; the strict
    // counter-number model rejects it as `extra_forbidden`.
    const { state_map: _strippedStateMap, ...semanticsRest } =
      widget.semantics as Record<string, unknown> & { state_map?: unknown };

    const updated: Widget = {
      ...widget,
      title: title.trim() || widget.title,
      image_url: widget.image_url ?? null,
      query: buildShotDataLineChartQuery({
        machineId: machine?.source_id ?? widget.query?.source ?? "",
        eventType: eventType as string,
        fields: series.reduce<Record<string, FieldType>>(
          (acc, _item, index) => {
            acc[`value_${index}`] = "float";
            return acc;
          },
          {},
        ),
        // The Range control owns the window: it rewrites it on every save, so
        // switching modes cannot leave the previous mode's keys behind.
        window: toWindow(range),
        bindings: series.map((item, index) => ({
          field: `value_${index}`,
          source: machine?.source_id ?? "",
          // The JSON Pointer the renderer resolves per event.
          address: item.address,
        })),
        pollingInterval: pollingInterval ?? DEFAULT_POLLING_INTERVAL,
      }),
      semantics: {
        ...(semanticsRest as WidgetSemantics),
        target_manual:
          saved.target_manual == null
            ? null
            : parseUserNumber(String(saved.target_manual)) ??
              saved.target_manual,
        min_value: manualYRange ? parseUserNumber(min) : null,
        max_value: manualYRange ? parseUserNumber(max) : null,
        thresholds: Object.keys(thresholds).length > 0 ? thresholds : null,
        show_min: saved.show_min === true || undefined,
        show_max: saved.show_max === true || undefined,
      },
      encoding: {
        ...widget.encoding,
        roles: series.reduce<Record<string, RoleBinding>>(
          (acc, item, index) => {
            acc[`value_${index}`] = {
              field: `value_${index}`,
              label: item.label,
              order: index,
              // Every line carries a colour, so this is normally set. Absent
              // only for a widget saved before colours existed: the chart then
              // uses its palette by series position.
              ...(item.color ? { color: item.color } : {}),
            };
            return acc;
          },
          {},
        ),
        // Scale entries are rebuilt from the series, index → value_N. Own every
        // value_N the widget had OR now has, so a removed series' stale scale
        // is dropped rather than preserved as "unrelated".
        ...scaleTransformsKey(
          widget.encoding?.transforms,
          Array.from(
            {
              length: Math.max(
                widget.query?.bindings?.length ?? 0,
                series.length,
              ),
            },
            (_value, index) => `value_${index}`,
          ),
          series.reduce<EncodingTransform[]>((acc, item, index) => {
            if (item.scale != null && item.scale !== 1) {
              acc.push({
                field: `value_${index}`,
                op: "scale",
                value: item.scale,
              });
            }
            return acc;
          }, []),
        ),
        min_roles: 1,
        max_roles: 5,
      },
      presentation: {
        ...widget.presentation,
        unit: unit?.unit_id ?? null,
      },
    };
    save(updated, { validate });
  };

  const handleCheckedIdsChange = (ids: string[]) => {
    // Series the newest event no longer carries stay selected — they are part
    // of the payload. They also count towards the cap, which is why a pick
    // that would exceed it is REFUSED instead of silently dropping one of
    // them: `encoding.max_roles` is 5, and losing a saved series to a click on
    // another field is worse than ignoring that click.
    const orphanIds = checkedIds.filter((id) => !tree?.items[id]);
    if (ids.length + orphanIds.length > MAX_SHOT_DATA_SERIES) {
      return;
    }
    // Unchecking flushes the custom name and colour, so re-checking starts from
    // the JSON key and the palette again.
    // A newly ticked field starts on a random palette colour, so no line is
    // ever colourless and the picker shows the truth from the first moment.
    const added = ids.filter((id) => !visibleCheckedIds.includes(id));
    if (added.length > 0) {
      setColors((prev) => {
        const next = { ...prev };
        for (const id of added) {
          if (!next[id]) {
            next[id] = pickPaletteHex(Object.values(next));
          }
        }
        return next;
      });
    }
    const removed = visibleCheckedIds.filter((id) => !ids.includes(id));
    if (removed.length > 0) {
      setLabels((prev) => {
        const copy = { ...prev };
        for (const id of removed) {
          delete copy[id];
        }
        return copy;
      });
      setColors((prev) => {
        const copy = { ...prev };
        for (const id of removed) {
          delete copy[id];
        }
        return copy;
      });
      setScales((prev) => {
        const copy = { ...prev };
        for (const id of removed) {
          delete copy[id];
        }
        return copy;
      });
    }
    setCheckedIds([
      // Keep the order fields were added in; orphans stay where they are.
      ...checkedIds.filter((id) => ids.includes(id) || !tree?.items[id]),
      // Newly ticked fields go last, not wherever the tree happens to list them.
      ...ids.filter((id) => !checkedIds.includes(id)),
    ]);
  };

  // Direct removal, used by the X button on each series card. Bypasses the
  // cap logic in `handleCheckedIdsChange` because removal can never
  // over-consume a slot — and crucially, it operates on `checkedIds` (where
  // orphans live), so an orphan card disappears the same as a visible one.
  // The tree unchecks itself via the derived `visibleCheckedIds`, so no
  // extra wiring is needed for the JSON tree.
  const removeSeries = (id: string) => {
    setCheckedIds((prev) => prev.filter((entry) => entry !== id));
    setLabels((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setColors((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setScales((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const selectedEventTypeOption =
    EVENT_TYPE_OPTIONS.find((option) => option.source_id === eventType) ?? null;

  const selectedUnitEntry = unit
    ? Object.entries(units).find(
        ([key, candidate]) =>
          key === unit.unit_id || candidate.unit_id === unit.unit_id,
      )
    : undefined;
  const selectedUnitOption = unit
    ? {
        name: unitOptionLabelOf(unit.unit_text?.eng, unit.unit_id),
        source_id: selectedUnitEntry?.[0] ?? unit.unit_id,
      }
    : null;

  if (isInitializing) {
    return (
      <div className={styles["shot-data-line-chart-editor__loading"]}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles["shot-data-line-chart-editor__root"]}>
      <Input2025
        inputPlaceHolder={i18n(`${NS}.titleInputPlaceholder`)}
        labelText={i18n(`${NS}.titleLabel`)}
        inputValue={title}
        onChangeFunc={(event) => setTitle(event.target.value)}
        inputVariant="dark-background"
      />

      <div className={styles["shot-data-line-chart-editor__field"]}>
        <DashboardEditWidgetDrawerDropdown
          options={machineOptions}
          selectedOption={machine}
          onSelectOption={handleMachineChange}
          labelText={i18n(`${NS}.machineLabel`)}
          noOptionsText={i18n(`${NS}.noMachines`)}
          placeholderText={i18n(`${NS}.machinesPlaceholder`)}
          isLoading={isLoadingMachines}
        />
      </div>

      <div className={styles["shot-data-line-chart-editor__field"]}>
        <DashboardEditWidgetDrawerDropdown
          options={EVENT_TYPE_OPTIONS}
          selectedOption={selectedEventTypeOption}
          onSelectOption={(selected) =>
            handleEventTypeChange(selected?.source_id ?? null)
          }
          labelText={i18n(`${NS}.eventTypeLabel`)}
          noOptionsText={i18n(`${NS}.noEventTypes`)}
          placeholderText={i18n(`${NS}.eventTypesPlaceholder`)}
          isLoading={false}
        />
      </div>

      {/* Range: the same three controls the other line charts use, plus the
          event-count mode this widget needs. The chosen mode decides the
          stored window, so only its own keys are written. */}
      <div>
        <span className={styles["shot-data-line-chart-editor__toggle-label"]}>
          {i18n(`${NS}.timeRangeLabel`)}
        </span>
        <div
          className={cn(
            styles["shot-data-line-chart-editor__toggle"],
            styles["shot-data-line-chart-editor__toggle--three"],
          )}
        >
          {(
            [
              ["duration", `${NS}.lookbackLabel`],
              ["timeframe", `${NS}.timeframeLabel`],
              ["events", `${NS}.rangeEventsLabel`],
            ] as [ShotDataRangeMode, string][]
          ).map(([mode, labelKey]) => (
            <button
              key={mode}
              type="button"
              className={cn(
                styles["shot-data-line-chart-editor__toggle-button"],
                {
                  [styles[
                    "shot-data-line-chart-editor__toggle-button--active"
                  ]]: range.mode === mode,
                },
              )}
              onClick={() => setRange((prev) => ({ ...prev, mode }))}
            >
              {i18n(labelKey)}
            </button>
          ))}
        </div>
      </div>

      {range.mode === "timeframe" && (
        <div className={styles["shot-data-line-chart-editor__row"]}>
          <DatePicker
            selectedDate={range.from}
            onDateChange={(date) =>
              setRange((prev) => ({ ...prev, from: date }))
            }
            placeholder={i18n(`${NS}.startLabel`)}
          />
          <DatePicker
            selectedDate={range.to}
            onDateChange={(date) => setRange((prev) => ({ ...prev, to: date }))}
            placeholder={i18n(`${NS}.endLabel`)}
          />
        </div>
      )}

      {range.mode === "duration" && (
        <div className={styles["shot-data-line-chart-editor__field"]}>
          <DashboardEditWidgetDrawerDropdown
            options={buildDurationOptions(LINE_CHART_DURATIONS_MS, i18n)}
            selectedOption={
              range.durationMs != null &&
              LINE_CHART_DURATIONS_MS.some((d) => d.ms === range.durationMs)
                ? findDurationOption(
                    LINE_CHART_DURATIONS_MS,
                    range.durationMs,
                    i18n,
                  )
                : null
            }
            onSelectOption={(selected) =>
              setRange((prev) => ({
                ...prev,
                durationMs: selected ? Number(selected.source_id) : null,
              }))
            }
            labelText={i18n(`${NS}.lookbackLabel`)}
            noOptionsText={i18n(`${NS}.noLookbacks`)}
            placeholderText={i18n(`${NS}.lookbackPlaceholder`)}
            isLoading={false}
          />
        </div>
      )}

      {range.mode === "events" && (
        <div className={styles["shot-data-line-chart-editor__field"]}>
          <Input2025
            type="number"
            labelText={i18n(`${NS}.rangeEventsLabel`)}
            inputPlaceHolder="10"
            inputValue={range.eventCount}
            onChangeFunc={(event) =>
              setRange((prev) => ({ ...prev, eventCount: event.target.value }))
            }
            inputVariant="dark-background"
          />
        </div>
      )}

      <div className={styles["shot-data-line-chart-editor__field"]}>
        <DashboardEditWidgetDrawerDropdown
          options={buildPollingOptions(i18n)}
          selectedOption={
            pollingInterval ? findPollingOption(pollingInterval, i18n) : null
          }
          onSelectOption={(selected) =>
            // null is observable (the dropdown re-shows its placeholder); the
            // save path applies DEFAULT_POLLING_INTERVAL when null comes
            // through. Mirrors trend-line's pattern.
            setPollingInterval(selected?.source_id ?? null)
          }
          labelText={i18n(`${NS}.pollingIntervalLabel`)}
          noOptionsText={i18n(`${NS}.noPollingIntervals`)}
          placeholderText={i18n(`${NS}.pollingIntervalPlaceholder`)}
          isLoading={false}
        />
      </div>

      {status === "loading" && (
        <div className={styles["shot-data-line-chart-editor__field-hint"]}>
          <span className={styles["shot-data-line-chart-editor__spinner"]}>
            <SpinnerWithVariant variant={["extra-small", "color-blue"]} />
          </span>
          {i18n(`${NS}.loading`)}
        </div>
      )}

      {status === "empty" && (
        <span className={styles["shot-data-line-chart-editor__field-hint"]}>
          {i18n(`${NS}.noData`)}
        </span>
      )}

      {status === "ready" && tree && (
        <>
          <span
            className={cn(styles["shot-data-line-chart-editor__field-hint"], {
              [styles["shot-data-line-chart-editor__field-hint--warning"]]:
                series.length >= MAX_SHOT_DATA_SERIES,
            })}
          >
            {/* Count comes from the payload, so the limit and the number of
                rows being drawn can never disagree. */}
            {i18n(`${NS}.selectValues`).replace(
              "{count}",
              `${series.length}/${MAX_SHOT_DATA_SERIES}`,
            )}
          </span>
          <JsonFieldTree
            tree={tree}
            checkedIds={visibleCheckedIds}
            onCheckedIdsChange={handleCheckedIdsChange}
            ariaLabel={i18n(`${NS}.treeLabel`)}
            searchPlaceholder={i18n(`${NS}.searchFieldsPlaceholder`)}
            searchAriaLabel={i18n(`${NS}.searchFieldsAriaLabel`)}
            noMatchesText={i18n(`${NS}.noMatches`)}
          />
          <div className={styles["shot-data-line-chart-editor__series-list"]}>
            {series.map((item) => (
              <div
                className={styles["shot-data-line-chart-editor__series-card"]}
                key={item.address}
              >
                <Input2025
                  labelText={item.address}
                  inputPlaceHolder={item.address}
                  inputValue={item.label}
                  onChangeFunc={(event) =>
                    setLabels((prev) => ({
                      ...prev,
                      [item.address]: event.target.value,
                    }))
                  }
                  inputVariant="dark-background"
                />
                <ColorSwatchPicker
                  label={i18n(`${NS}.colorLabel`)}
                  value={item.color}
                  swatches={TREND_LINE_PALETTE}
                  onChange={(color) =>
                    setColors((prev) => ({ ...prev, [item.address]: color }))
                  }
                />
                <ScaleInput
                  value={scales[item.address] ?? "1"}
                  onChange={(raw) =>
                    setScales((prev) => ({
                      ...prev,
                      [item.address]: raw,
                    }))
                  }
                />
                <button
                  type="button"
                  className={
                    styles["shot-data-line-chart-editor__series-remove"]
                  }
                  onClick={() => removeSeries(item.address)}
                  aria-label={i18n(`${NS}.removeSeries`)}
                >
                  <img src={closeIcon} alt="" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={styles["shot-data-line-chart-editor__field"]}>
        <DashboardEditWidgetDrawerDropdown
          options={Object.entries(units).map(([key, value]) => {
            const text =
              value.unit_text?.eng ?? Object.values(value.unit_text ?? {})[0];
            return {
              name: unitOptionLabelOf(text, value.unit_id),
              searchText: [
                value.unit_id,
                value.external_reference,
                text?.name,
                text?.symbol,
                text?.description,
              ]
                .filter(Boolean)
                .join(" "),
              source_id: key,
            };
          })}
          selectedOption={selectedUnitOption}
          onSelectOption={handleUnitSelection}
          labelText={i18n(`${NS}.unitLabel`)}
          noOptionsText={i18n(`${NS}.noUnits`)}
          placeholderText={i18n(`${NS}.unitsPlaceholder`)}
          isLoading={isLoadingUnits}
        />
      </div>

      <>
        {/* Manual Y range. Off = automatic axis (the default). Ticking is the
              explicit control; unticking resets to automatic on save. */}
        <div className={styles["shot-data-line-chart-editor__field"]}>
          <CheckBoxWithText
            text={i18n(`${NS}.manualYRangeLabel`)}
            checkboxState={manualYRange}
            handleCheckBoxClick={() => setManualYRange((prev) => !prev)}
          />
        </div>
        {manualYRange && (
          <div className={styles["shot-data-line-chart-editor__row"]}>
            <Input2025
              labelText={i18n(`${NS}.yMinLabel`)}
              inputPlaceHolder={i18n(`${NS}.yMinLabel`)}
              inputValue={min}
              onChangeFunc={(event) => setMin(event.target.value)}
              inputVariant="dark-background"
            />
            <Input2025
              labelText={i18n(`${NS}.yMaxLabel`)}
              inputPlaceHolder={i18n(`${NS}.yMaxLabel`)}
              inputValue={max}
              onChangeFunc={(event) => setMax(event.target.value)}
              inputVariant="dark-background"
            />
            <button
              type="button"
              className={styles["shot-data-line-chart-editor__add-line"]}
              onClick={() => {
                setMin("");
                setMax("");
                setManualYRange(false);
              }}
            >
              {i18n(`${NS}.resetYRangeLabel`)}
            </button>
          </div>
        )}
      </>

      <Spacer16 />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={i18n(`${NS}.saveButton`)}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={i18n(`${NS}.cancelButton`)}
          variant="secondary"
          // Cancel must NOT fire mid-save: it would dispatch removePendingWidget
          // while POST/PUT are still in flight and leak a server-side orphan.
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
